import { useEffect, useState } from "react";
import { Mail, Loader2, Send, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { NavHeader } from "../components/NavHeader";
import { Footer } from "../components/Footer";
import { Disclaimer } from "../components/Disclaimer";
import { toast } from "sonner";

export const ContactPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    document.title = "Contact · Astrarium";
  }, []);

  const valid =
    name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(email) &&
    message.trim().length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || sending) return;
    setSending(true);
    // Open the user's mail client with the message pre-filled. We do not run
    // a backend email service yet, so this is the safest, no-account flow.
    const subject = encodeURIComponent(`[Astrarium] ${topic || "Message"} from ${name}`);
    const body = encodeURIComponent(
      `Name: ${name}\nReply-to: ${email}\nTopic: ${topic || "none"}\n\n${message}`
    );
    setTimeout(() => {
      window.location.href = `mailto:hello@astrarium.app?subject=${subject}&body=${body}`;
      setSent(true);
      setSending(false);
      toast.success("Your mail client should open now. Press send.");
    }, 400);
  };

  return (
    <>
      <NavHeader />
      <main
        data-testid="contact-page"
        className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12"
      >
        <p className="text-stardust/70 uppercase tracking-[0.5em] text-[10px] mb-5 text-center">Write to us</p>
        <h1 className="font-display text-4xl sm:text-6xl text-[#F5EBD8] text-center leading-tight mb-5">
          Send the wheel a note<span className="text-stardust">.</span>
        </h1>
        <p className="font-serif-italic text-slate-300/90 text-lg sm:text-xl leading-relaxed text-center mb-12 max-w-2xl mx-auto">
          For takedown requests, partnerships, bug reports, or thoughtful disagreements.
          We read every note and reply within a few days.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-center">
            <Mail className="h-4 w-4 text-stardust mx-auto mb-2" />
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">Email</p>
            <a
              href="mailto:hello@astrarium.app"
              data-testid="contact-email-link"
              className="text-stardust text-sm hover:text-stardust-light break-all"
            >
              hello@astrarium.app
            </a>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">Response time</p>
            <p className="text-slate-200 text-sm">Usually within 2 to 4 working days.</p>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-2">Take down a share</p>
            <p className="text-slate-200 text-sm">Send us the share URL and we will remove it.</p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="contact-form"
          className="rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-6 sm:p-8 space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] mb-2 block">Your name</Label>
              <Input
                data-testid="contact-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-stardust focus-visible:border-stardust"
                required
              />
            </div>
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] mb-2 block">Your email</Label>
              <Input
                data-testid="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-stardust focus-visible:border-stardust"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] mb-2 block">Topic (optional)</Label>
            <Input
              data-testid="contact-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Partnership, takedown, bug, feedback..."
              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 h-12 focus-visible:ring-stardust focus-visible:border-stardust"
            />
          </div>
          <div>
            <Label className="text-slate-400 text-xs uppercase tracking-[0.2em] mb-2 block">Message</Label>
            <Textarea
              data-testid="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="bg-black/30 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-stardust focus-visible:border-stardust resize-none"
              required
            />
          </div>
          <Button
            type="submit"
            data-testid="contact-submit"
            disabled={!valid || sending}
            className="bg-stardust hover:bg-stardust-light text-cosmic-900 font-medium uppercase tracking-[0.3em] text-[11px] px-8 py-5 rounded-md disabled:opacity-40 transition-all"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : sent ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Mail client opened
              </>
            ) : (
              <>
                Send
                <Send className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
          <p className="text-slate-500 text-xs leading-relaxed">
            We use a mailto link here on purpose. Your message opens in your own email
            client so it never travels through a third-party form provider. If your
            device cannot open mailto, write to us at <a className="text-stardust hover:text-stardust-light" href="mailto:hello@astrarium.app">hello@astrarium.app</a> directly.
          </p>
        </form>

        <Disclaimer />
      </main>
      <Footer />
    </>
  );
};
